/**
 * Routes, defined in code rather than by file convention.
 *
 * TanStack Router's file-based mode would add a generator and a build step for a route tree
 * this size; code-based routes keep the whole map readable in one screen, which is worth
 * more here than the convention.
 *
 * Each route is created explicitly rather than through a helper, because the literal path
 * is what gives `<Link to="...">` its type safety — a helper that took `path: string` would
 * erase exactly the guarantee this file exists to provide.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from "@tanstack/react-router";
import { Layout } from "./Layout.js";
import { AuthorityReports } from "../screens/AuthorityReports.js";
import { Billing } from "../screens/Billing.js";
import { CaseDetail } from "../screens/CaseDetail.js";
import { CaseList } from "../screens/CaseList.js";
import { ClientCard } from "../screens/ClientCard.js";
import { ClientImport } from "../screens/ClientImport.js";
import { ClientList } from "../screens/ClientList.js";
import { Dashboard } from "../screens/Dashboard.js";
import { DeadlineCalendar } from "../screens/DeadlineCalendar.js";
import { Documents } from "../screens/Documents.js";
import { Members } from "../screens/Members.js";
import { OfficeControl } from "../screens/OfficeControl.js";
import { Profitability } from "../screens/Profitability.js";
import { Reminders } from "../screens/Reminders.js";
import { ReportingMatrix } from "../screens/ReportingMatrix.js";
import { Settings } from "../screens/Settings.js";
import { Tasks } from "../screens/Tasks.js";
import { TimeSheet } from "../screens/TimeSheet.js";

declare const __SHOW_DEV_LOGINS__: boolean;

const rootRoute = createRootRoute({ component: Outlet });

/**
 * The shell wraps every office screen, so the rail, the firm switcher and the running
 * timer survive navigation. A pathless layout route rather than a prefix, because the URLs
 * should not carry a `/app` segment nobody needs.
 */
const shell = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: Layout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => shell,
  path: "/",
  component: Dashboard,
});
const clientsRoute = createRoute({
  getParentRoute: () => shell,
  path: "/clients",
  component: ClientList,
});
const clientImportRoute = createRoute({
  getParentRoute: () => shell,
  path: "/clients/import",
  component: ClientImport,
});
const clientCardRoute = createRoute({
  getParentRoute: () => shell,
  path: "/clients/$clientId",
  component: ClientCard,
});
const reportingRoute = createRoute({
  getParentRoute: () => shell,
  path: "/reporting",
  component: ReportingMatrix,
});
const authorityReportsRoute = createRoute({
  getParentRoute: () => shell,
  path: "/authority-reports",
  component: AuthorityReports,
});
const calendarRoute = createRoute({
  getParentRoute: () => shell,
  path: "/calendar",
  component: DeadlineCalendar,
});
const annualRoute = createRoute({
  getParentRoute: () => shell,
  path: "/annual",
  component: () => <CaseList kind="annual" />,
});
const annualCaseRoute = createRoute({
  getParentRoute: () => shell,
  path: "/annual/$caseId",
  component: () => <CaseDetail kind="annual" />,
});
const wealthRoute = createRoute({
  getParentRoute: () => shell,
  path: "/wealth",
  component: () => <CaseList kind="wealth" />,
});
const wealthCaseRoute = createRoute({
  getParentRoute: () => shell,
  path: "/wealth/$caseId",
  component: () => <CaseDetail kind="wealth" />,
});
const documentsRoute = createRoute({
  getParentRoute: () => shell,
  path: "/documents",
  component: Documents,
});
const tasksRoute = createRoute({ getParentRoute: () => shell, path: "/tasks", component: Tasks });
const timeRoute = createRoute({ getParentRoute: () => shell, path: "/time", component: TimeSheet });
const billingRoute = createRoute({
  getParentRoute: () => shell,
  path: "/billing",
  component: Billing,
});
const profitabilityRoute = createRoute({
  getParentRoute: () => shell,
  path: "/profitability",
  component: Profitability,
});
const officeRoute = createRoute({
  getParentRoute: () => shell,
  path: "/office",
  component: OfficeControl,
});
const remindersRoute = createRoute({
  getParentRoute: () => shell,
  path: "/reminders",
  component: Reminders,
});
const membersRoute = createRoute({
  getParentRoute: () => shell,
  path: "/members",
  component: Members,
});
const settingsRoute = createRoute({
  getParentRoute: () => shell,
  path: "/settings",
  component: Settings,
});

/**
 * The component gallery, dev-only — and genuinely absent from a production build.
 *
 * A runtime guard was not enough: the route redirected, but the component was still
 * imported statically and shipped in the bundle. Because `__SHOW_DEV_LOGINS__` is a
 * compile-time constant derived from Vite's `command`, this branch is dead code in a build,
 * so Rollup removes the dynamic import with it and the chunk is never emitted.
 */
const devRoutes = __SHOW_DEV_LOGINS__
  ? [
      createRoute({
        getParentRoute: () => shell,
        path: "/gallery",
        component: lazyRouteComponent(() => import("../screens/Gallery.js"), "Gallery"),
      }),
      createRoute({
        getParentRoute: () => shell,
        path: "/dev",
        component: lazyRouteComponent(() => import("../screens/DevConsole.js"), "DevConsole"),
      }),
    ]
  : [];

const routeTree = rootRoute.addChildren([
  shell.addChildren([
    dashboardRoute,
    clientsRoute,
    // Declared before the ":clientId" route so "/clients/import" is not read as an id.
    clientImportRoute,
    clientCardRoute,
    reportingRoute,
    authorityReportsRoute,
    calendarRoute,
    annualRoute,
    annualCaseRoute,
    wealthRoute,
    wealthCaseRoute,
    documentsRoute,
    tasksRoute,
    timeRoute,
    billingRoute,
    profitabilityRoute,
    officeRoute,
    remindersRoute,
    membersRoute,
    settingsRoute,
    ...devRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Financial screens: a stale render from a preload would be worse than a spinner.
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
